import { Field, Form, Formik } from 'formik';
import { Map } from 'immutable';
import _ from 'lodash';
import { DateTime, Interval } from 'luxon';
import PropTypes from 'prop-types';
// CORE
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import Button from '@mui/material/Button';
// MUI
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';

// AC
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { filtersActions, filtersTypes } from 'app-components/flux/filters';
import {
  spendingWatchlistActions,
  spendingWatchlistSelectors,
  spendingWatchlistTypes,
} from 'app-components/flux/spending-watchlist';
import { tracker } from 'app-components/utils/core';

// components
import Typography from '@mui/material/Typography';
import QAmountField from 'components/QAmountField';
import QDateRangePicker from 'components/QDateRangePicker';
import QEmojiSelect from 'components/QEmojiSelect';
import TypeSelectField from './TypeSelectField';

const useStyles = makeStyles()(({ spacing }, props) => ({
  root: {
    padding: '15px 120px 0 176px',
    marginBottom: 70,
    flexDirection: 'column',
  },
  submitButton: {
    position: 'absolute',
    bottom: 24,
    right: 24,
  },
  typeContainer: {
    width: 450,
    ...(!props.isEditing ? { marginBottom: 16 } : {}),
  },
  nameContainer: {
    width: 312,
  },
  emojiContainer: {
    width: 80,
    marginRight: spacing(2),
  },
  radioButtonWithCaption: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 24,
  },
  captionRadio: {
    marginLeft: 30,
    marginTop: -12,
  },
  showTargetAmountGroup: {
    flexDirection: 'row',
    marginTop: 16,
    '& label + label': {
      marginLeft: spacing(2),
    },
  },
  targetAmountInput: {
    textAlign: 'left',
    // height: 35,
  },
  notVisible: {
    pointerEvents: 'none',
    visibility: 'hidden',
  },
  textField: {
    width: '100%',
    '& input': {
      cursor: 'pointer',
    },
  },
}));

const filterKeyObj = {
  CHARTOFACCOUNT: 'chartOfAccounts',
  PAYEE: 'payees',
  TAG: 'tags',
};

function WatchlistForm(props) {
  const { typesData, watchlist, isEditing, filter, setFormData, overwriteInitialData, setSuccessModal, onSubmit } =
    props;
  const { classes, cx } = useStyles({ isEditing });
  const swlStartDate = watchlist?.period === 'FIXED' ? DateTime.fromISO(watchlist.startDate) : null;
  const swlEndDate = watchlist?.period === 'FIXED' ? DateTime.fromISO(watchlist.endDate) : null;

  const [anchorEl, setAnchorEl] = useState(null);
  const [interval, setInterval] = useState(
    watchlist?.period === 'FIXED' ? Interval.fromDateTimes(swlStartDate, swlEndDate) : null,
  );
  const [finalInterval] = useState(interval);

  const [debouncedShareFormikProps] = useDebouncedCallback((formData) => setFormData?.(formData), 300);
  const showTimeTracking = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'watchlistTimeSelector'));
  const watchlistList = useSelector(spendingWatchlistSelectors.getSpendingWatchLists);
  const today = useSelector(dateSelectors.getCurrentDate);

  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    if (overwriteInitialData) {
      const typeId = overwriteInitialData.filter.type;
      const filterTypeKey = filterKeyObj[typeId];
      const resolvedType = typeof filter === 'string' ? filter : typeId;
      return {
        ...overwriteInitialData,
        filter: {
          ...overwriteInitialData.filter,
          type: resolvedType,
          [filterTypeKey]: overwriteInitialData.filter?.[filterTypeKey] || [],
        },
      };
    }
    return {
      name: isEditing ? watchlist.name : '',
      emoji: isEditing ? watchlist.emoji : '',
      // this should be refactored eventually and filter should be the same obj type
      filter: isEditing ? filter.filterItems[0] : { type: filter },
      showTargetAmount: (isEditing ? Boolean(watchlist.targetAmount != null) : false).toString(), // boolean as a string, kinda odd
      targetAmount: isEditing && watchlist.targetAmount != null ? watchlist.targetAmount.toString() : '0.00',
      timeTracking: (isEditing ? watchlist.period === 'FIXED' : false).toString(),
    };
  }, [watchlist, isEditing, filter, overwriteInitialData]);

  const renderForm = useCallback(
    (formikProps) => {
      debouncedShareFormikProps({
        dirty: formikProps.dirty,
        isSubmitting: formikProps.isSubmitting,
        isValid: formikProps.isValid,
        isValidating: formikProps.isValidating,
        resetForm: formikProps.resetForm,
        submitForm: formikProps.submitForm,
        values: formikProps.values,
      });

      const genericOnChange = (field, value) => formikProps.setFieldValue(field.name, value);

      const alreadyUsedWatchlistName = (newName) => {
        const nameToUse = newName || formikProps.values.name;
        let isNameUsed = watchlistList.find((p) => p.name === nameToUse?.trim()) !== undefined;
        if (isEditing && formikProps.values?.name === watchlist?.name) {
          isNameUsed = false;
        }
        return isNameUsed;
      };

      const typeId = formikProps.values.filter?.type;
      const filterTypeKey = filterKeyObj[typeId];
      const dynamicFieldValue = formikProps.values.filter?.[filterTypeKey];

      // Checks
      const isNameEmpty = !formikProps.values.name || formikProps.values.name.trim().length === 0;
      const isDynamicFieldEmpty = !Array.isArray(dynamicFieldValue) || dynamicFieldValue.length === 0;
      const isDuplicateName = alreadyUsedWatchlistName();
      const hasOldInvalidConditions = !formikProps.isValid || (!formikProps.dirty && !overwriteInitialData);
      const isTimeTrackingButNoInterval = showTimeTracking && formikProps.values.timeTracking === 'true' && !interval;

      const [singleType, pluralType, qResPickerKey] = typesData[typeId];

      let isDisabled =
        isNameEmpty || isDynamicFieldEmpty || isDuplicateName || hasOldInvalidConditions || isTimeTrackingButNoInterval;

      const getUpperBoundInDays = (count, dateInterval) => {
        const upperBound = today.plus({ [dateInterval]: [count] });
        return Math.ceil(upperBound.diff(today, 'days').days);
      };

      return (
        <Form>
          <Grid item container>
            <Grid item container direction='column' className={classes.typeContainer}>
              {isEditing ? (
                <Field name='filter.type'>
                  {({ field, form: { touched, errors } }) => (
                    <TextField
                      {...field}
                      id='watchlist-input-typeId'
                      label='Type'
                      error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                      helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                      InputProps={{
                        autoComplete: 'off',
                      }}
                      variant='filled'
                      margin='normal'
                      fullWidth
                      select
                    >
                      {Object.keys(typesData).map((typeKey) => (
                        <MenuItem key={typeKey} value={typeKey}>
                          By {typesData[typeKey][0]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Field>
              ) : (
                <>
                  <Typography variant='titleMedium'>By {singleType}</Typography>
                  <Typography variant='bodyRegular' color={'textSecondary'}>
                    Keep an eye on your spending for one or more {pluralType}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid item className={classes.emojiContainer}>
              <Field name='emoji'>
                {({ field, form: { touched, errors } }) => (
                  <QEmojiSelect
                    {...field}
                    id='watchlist-input-emoji'
                    onChange={(value) => genericOnChange(field, value)}
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                    helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                  />
                )}
              </Field>
            </Grid>
            <Grid item className={classes.nameContainer}>
              <Field name='name'>
                {({ field, form: { touched, errors } }) => (
                  <TextField
                    {...field}
                    id='watchlist-input-name'
                    label='Name'
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                    helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                    InputProps={{
                      autoComplete: 'off',
                    }}
                    variant='filled'
                    margin='normal'
                    fullWidth
                    autoFocus={!isEditing}
                  />
                )}
              </Field>
            </Grid>
          </Grid>
          <Grid item container direction='column'>
            <Field name={`filter.${filterTypeKey}`}>
              {({ field, form: { touched, errors } }) => (
                <TypeSelectField
                  {...field}
                  id={`watchlist-input-select-${pluralType.toLowerCase()}`}
                  label={`Select ${pluralType}`}
                  fakeValue={`Select ${pluralType}`}
                  onBlur={() => formikProps.setFieldTouched(`filter.${filterTypeKey}`)}
                  onChange={(value, name) => {
                    if (formikProps.values.name?.trim().length === 0) {
                      formikProps.setValues({
                        ...formikProps.values,
                        name: value[0]?.id === '0' ? 'Uncategorized' : name,
                        filter: { ...(isEditing ? initialValues.filter : { type: filter }), [filterTypeKey]: value },
                      });
                    } else {
                      genericOnChange(field, value);
                    }
                  }}
                  error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                  helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                  itemsType={qResPickerKey}
                  filter={formikProps.values.filter}
                  filterTypeLabel={pluralType}
                  filterTypeKey={filterTypeKey}
                />
              )}
            </Field>
          </Grid>
          <Grid item container direction='column' style={{ marginTop: 24 }}>
            {showTimeTracking && (
              <>
                <Typography variant='bodyRegular'>Time tracking</Typography>
                <Field name='timeTracking'>
                  {({ field, form: { _touched, _errors } }) => (
                    <RadioGroup
                      {...field}
                      id='watchlist-input-time-tracking'
                      aria-label='time tracking'
                      className={classes.showTargetAmountGroup}
                    >
                      <div className={classes.radioButtonWithCaption}>
                        <FormControlLabel
                          value={'false'}
                          control={<Radio />}
                          label='Ongoing'
                          onChange={() => {
                            formikProps.setFieldValue('timeInterval', undefined);
                          }}
                        />
                        <Typography variant='captionRegular' className={classes.captionRadio}>
                          rolling 12 months
                        </Typography>
                      </div>
                      <div className={classes.radioButtonWithCaption}>
                        <FormControlLabel
                          value={'true'}
                          control={<Radio />}
                          label='Fixed'
                          onChange={() => {
                            if (
                              (formikProps.values.timeInterval && !interval?.equals(formikProps.values.timeInterval)) ||
                              !finalInterval?.equals(interval)
                            ) {
                              formikProps.setFieldValue('timeInterval', interval);
                            }
                          }}
                        />
                        <Typography variant='captionRegular' className={classes.captionRadio}>
                          exact date range
                        </Typography>
                      </div>
                    </RadioGroup>
                  )}
                </Field>
                <Field name='timeInterval'>
                  {({ _field, form: { _touched, _errors } }) => (
                    <QDateRangePicker
                      anchorEl={anchorEl}
                      onSelect={(result) => {
                        setAnchorEl(null);
                        if ((result.interval && !interval?.equals(result.interval)) || interval === null) {
                          formikProps.setFieldValue('timeInterval', result.interval);
                          setInterval(result.interval);
                        }
                      }}
                      compact
                      range
                      maxDaysAfter={getUpperBoundInDays(24, 'months')}
                      allowCustomRange
                      id={'watchlist-date-range-picker'}
                      onClose={() => setAnchorEl(null)}
                      startingDate={interval?.start}
                      endingDate={interval?.end}
                      popoverProps={{
                        transformOrigin: {
                          horizontal: 'center',
                          vertical: 'top',
                        },
                        anchorOrigin: {
                          vertical: 'center',
                          horizontal: 'center',
                        },
                      }}
                      renderInput={(onClickCallback) => (
                        <TextField
                          variant={'filled'}
                          onClick={onClickCallback}
                          label={'Select date range'}
                          margin={'normal'}
                          value={interval?.toFormat('LLL dd, yyyy') || ''}
                          className={
                            formikProps.values.timeTracking === 'true' ? classes.textField : classes.notVisible
                          }
                        />
                      )}
                    />
                  )}
                </Field>
              </>
            )}
          </Grid>
          <Grid item container direction='column' style={{ marginTop: 8 }}>
            <Typography variant='bodyRegular'>Target</Typography>
            <Field name='showTargetAmount'>
              {({ field, form: { _touched, _errors } }) => (
                <RadioGroup
                  {...field}
                  id='watchlist-input-show-targetAmount'
                  aria-label='Monthly Target'
                  className={classes.showTargetAmountGroup}
                >
                  <FormControlLabel value='false' control={<Radio />} label='Don’t Set' />
                  <FormControlLabel value='true' control={<Radio />} label='Set' />
                </RadioGroup>
              )}
            </Field>
            <Field name='targetAmount'>
              {({ field, form: { touched, errors } }) => (
                <QAmountField
                  {...field}
                  label='Amount'
                  expectedSign='positive'
                  noNegatives
                  className={cx(
                    classes.targetAmountInput,
                    formikProps?.values?.showTargetAmount === 'false' && classes.notVisible,
                  )}
                  editable
                  id='watchlist-input-targetAmount'
                  fullWidth
                  margin='normal'
                  onChange={(e) => formikProps.setFieldValue('targetAmount', e?.target?.value)}
                  error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                  helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                />
              )}
            </Field>
          </Grid>

          <Button
            className={classes.submitButton}
            variant='contained'
            width={85}
            type='submit'
            disabled={isDisabled}
            onClick={() => setImmediate(() => formikProps.setFieldTouched(`filter.${filterTypeKey}`))}
          >
            {isEditing ? 'Save' : 'Create'}
          </Button>
        </Form>
      );
    },
    [
      debouncedShareFormikProps,
      overwriteInitialData,
      showTimeTracking,
      interval,
      typesData,
      classes.typeContainer,
      classes.emojiContainer,
      classes.nameContainer,
      classes.submitButton,
      classes.showTargetAmountGroup,
      classes.radioButtonWithCaption,
      classes.captionRadio,
      classes.textField,
      classes.notVisible,
      classes.targetAmountInput,
      isEditing,
      watchlistList,
      watchlist?.name,
      today,
      initialValues.filter,
      filter,
      finalInterval,
      anchorEl,
      cx,
    ],
  );

  const onCreate = useCallback(
    (values, options = {}) => {
      const { showSuccessModal = true } = options;
      const newId = uuidv4().toUpperCase();
      const { type } = values.filter;
      const filterTypeKey = filterKeyObj[type];
      const targetAmount = values.showTargetAmount === 'true' ? values.targetAmount : null;
      const newList = Map({
        title: values.name.length >= 256 ? values.name.substring(0, 256).trim() : values.name.trim(),
        targetAmount,
        filterItems: filtersTypes.mkFilter({
          id: null,
          clientId: newId,
          type: 'SPENDINGWATCHLIST',
          filterItems: [
            {
              type,
              [filterTypeKey]: values.filter[filterTypeKey] || [],
            },
          ],
        }),
        emoji: values.emoji,
        period: values.timeTracking === 'true' ? 'FIXED' : 'MONTH',
        startDate: showTimeTracking ? interval?.start?.toISO() : null,
        endDate: showTimeTracking ? interval?.end?.toISO() : null,
      });
      dispatch(spendingWatchlistActions.createWatchList(newList));
      if (showSuccessModal) {
        setSuccessModal?.('create');
      }
      let watchlistType;
      switch (typesData[values.filter.type][1]) {
        case 'Payees':
          watchlistType = 'payee';
          break;
        case 'Tags':
          watchlistType = 'tag';
          break;
        default:
          watchlistType = 'category';
      }

      tracker.track(tracker.events.watchlistCreate, {
        type: watchlistType,
        target: Boolean(targetAmount),
        emoji: !!values.emoji,
        number: values.filter?.[filterTypeKey]?.length || 0,
      });
    },
    [typesData, dispatch, interval, setSuccessModal, showTimeTracking],
  );

  const onUpdate = useCallback(
    (values, options = {}) => {
      const { showSuccessModal = true } = options;
      const baseFilterData = { ...filter.toJS() };
      delete baseFilterData.id;
      delete baseFilterData.createdAt;
      const filterToCheck = { ...baseFilterData, filterItems: [values.filter] };
      if (JSON.stringify(baseFilterData) !== JSON.stringify(filterToCheck)) {
        const { type } = values.filter;
        const filterTypeKey = filterKeyObj[type];

        dispatch(
          filtersActions.updateFilter(
            {
              ...filter.toJS(),
              filterItems: [
                {
                  type,
                  [filterTypeKey]: values.filter[filterTypeKey] || [],
                },
              ],
            },
            { context: 'watchlist' },
          ),
        );
      }

      const resetTargetAmount = !values.targetAmount && values.targetAmount !== undefined;

      const updates = {};
      if (values.name.trim() !== watchlist.name.trim()) {
        updates.name = values.name.trim();
      }
      if (values.targetAmount !== watchlist.targetAmount && values.targetAmount !== null) {
        updates.targetAmount = values.targetAmount;
      }
      if (values.emoji !== watchlist.emoji) {
        updates.emoji = values.emoji;
      }
      if (resetTargetAmount) {
        updates.resetTargetAmount = resetTargetAmount;
        updates.targetAmount = null;
      }

      if (values.showTargetAmount === 'false') {
        updates.resetTargetAmount = true;
        updates.targetAmount = null;
      }
      const currentPeriod = values.timeTracking === 'true' ? 'FIXED' : 'MONTH';
      const watchlistStartDate = currentPeriod === 'FIXED' && DateTime.fromISO(watchlist.startDate);
      const watchlistEndDate = currentPeriod === 'FIXED' && DateTime.fromISO(watchlist.endDate);
      const currentStartDate = interval?.start;
      const currentEndDate = interval?.end;

      if (watchlist.period !== currentPeriod) {
        updates.period = currentPeriod;
      }
      if (
        showTimeTracking &&
        (currentStartDate?.month !== watchlistStartDate?.month || currentStartDate?.day !== watchlistStartDate?.day)
      ) {
        updates.startDate = currentStartDate.toISO();
      }
      if (
        showTimeTracking &&
        (currentEndDate?.month !== watchlistEndDate?.month || currentEndDate?.day !== watchlistEndDate?.day)
      ) {
        updates.endDate = currentEndDate.toISO();
      }

      dispatch(
        spendingWatchlistActions.updateSpendingWatchList(
          spendingWatchlistTypes.mkSpendingWatchList({ ...watchlist, ...updates }),
        ),
      );

      if (showSuccessModal) {
        setSuccessModal?.('update');
      }
      tracker.track(tracker.events.watchlistEdit);
    },
    [dispatch, filter, watchlist, setSuccessModal, interval, showTimeTracking],
  );

  const handleSubmit = useCallback(
    (values) => {
      const submitDefault = isEditing ? onUpdate : onCreate;

      if (onSubmit) {
        return onSubmit(values, submitDefault);
      }

      return submitDefault(values);
    },
    [isEditing, onCreate, onSubmit, onUpdate],
  );

  return (
    <Grid container className={classes.root}>
      <Formik
        key={`watchlist-form-${isEditing ? 'editing' : 'creating'}`}
        initialValues={initialValues}
        validateOnChange
        validate={(values) => {
          const errors = {};

          if (!values.filter?.type) {
            errors.filter = { type: 'required' };
          }

          if (!values?.name?.trim()) {
            errors.name = 'required';
          } else {
            const name = values.name.trim();
            if (!(isEditing && name === watchlist.name)) {
              if (watchlistList.find((p) => p.name === name)) {
                // name collision
                errors.name = 'the watchlist must have a unique name';
              }
            }
          }

          const typeKey = filterKeyObj[values?.filter?.type];
          if (!typeKey || !values.filter[typeKey]?.length) {
            errors.filter = { ...(errors.filter || {}), [typeKey]: 'required' };
          }

          if (values.showTargetAmount === 'true' && values.targetAmount == null) {
            errors.targetAmount = 'required';
          }

          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {renderForm}
      </Formik>
    </Grid>
  );
}

WatchlistForm.propTypes = {
  typesData: PropTypes.object,
  watchlist: PropTypes.object,
  isEditing: PropTypes.bool,
  filter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setFormData: PropTypes.func,
  overwriteInitialData: PropTypes.object,
  setSuccessModal: PropTypes.func,
  onSubmit: PropTypes.func,
};

WatchlistForm.whyDidYouRender = true;

export default WatchlistForm;
